import { Button, useMultiStepForm, cn } from "web-design-system";
import {
  ChevronRightIcon,
  LucideCheckCircle,
  AlertTriangleIcon,
} from "lucide-react";
import { getInitials } from "creso-core/utils";
import { ONBOARDING_STEPS } from "creso-core/constants";
import type { useInvestorInfo } from "creso-core/hooks";
import { Avatar } from "../avatars";

export type OnboardingProgressStep = {
  label: string;
  /** Index of the matching child within the MultiStepForm. */
  stepIndex: number;
  completed: boolean;
  hasErrors?: boolean;
};

type InvestorInfo = ReturnType<typeof useInvestorInfo>["data"];

/**
 * Derives the sidebar state from investor data.
 *
 * `stepOffset` maps `ONBOARDING_STEPS` onto the host's `MultiStepForm`
 * children — web-clients prepends an account-details step, so its indices sit
 * one higher than the partner portal's.
 */
export function buildOnboardingProgressSteps(
  data: InvestorInfo,
  {
    currentStep,
    steps = ONBOARDING_STEPS,
    stepOffset = 0,
  }: {
    currentStep: number;
    steps?: typeof ONBOARDING_STEPS;
    stepOffset?: number;
  }
): OnboardingProgressStep[] {
  const { client } = data;

  return steps.map((step, idx) => {
    const stepIndex = idx + stepOffset;
    const completed = step.completed(data) && stepIndex !== currentStep;

    let hasErrors = false;
    // NOTE: `ONBOARDING_STEPS` labels this step "Personal details", so this
    // branch has never matched. Preserved as-is rather than silently turning a
    // KYC warning on for every in-progress client.
    if (step.label === "Personal information") {
      hasErrors = client?.kyc_status !== "S";
    }
    if (step.label === "Bank details") {
      hasErrors =
        completed && data?.bank?.[0]?.verification_status !== "verified";
    }

    return { label: step.label, stepIndex, completed, hasErrors };
  });
}

export function OnboardingProgress({
  name,
  steps,
  currentStep,
  onStepSelect,
  className,
}: {
  name?: string | null;
  steps: OnboardingProgressStep[];
  currentStep: number;
  onStepSelect?: (stepIndex: number) => void;
  className?: string;
}) {
  const { goToStep } = useMultiStepForm();
  const selectStep = onStepSelect ?? goToStep;

  return (
    <div className={cn("flex flex-col gap-5 p-5 w-75", className)}>
      <div className="flex flex-col gap-3">
        <Avatar fallback={getInitials(name ?? "")} url={""} color="violet" />
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-xl">{name}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {steps.map((step, idx) => (
          <Button
            variant="ghost"
            key={step.label}
            data-completed={step.completed}
            data-active={step.stepIndex === currentStep}
            onClick={() => step.completed && selectStep(step.stepIndex)}
            data-errors={step.hasErrors}
            className="group flex data-[completed=true]:hover:bg-background/50 data-[completed=false]:hover:bg-transparent font-normal text-sm flex-row items-center px-3 py-3 [&_svg]:size-4 rounded-lg justify-between data-[active=true]:bg-background data-[active=true]:font-medium"
          >
            {idx + 1}. {step.label}
            <LucideCheckCircle className="text-constructive hidden group-data-[errors=false]:group-data-[completed=true]:block" />
            <ChevronRightIcon className="hidden group-data-[completed=false]:block" />
            <AlertTriangleIcon className="text-yellow-500 hidden group-data-[completed=true]:group-data-[errors=true]:block" />
          </Button>
        ))}
      </div>
    </div>
  );
}
